import { z } from "zod";
import { parisToday } from "./dates";
import { listQuerySchema } from "./schemas";
import { uniqueBy } from "./unique";

export const STOCK_LEVELS = ["out", "low", "ok"] as const;
export type StockLevel = (typeof STOCK_LEVELS)[number];

/** Physical articles by default; kits (compositions) have a derived stock and their own view. */
export const STOCK_VIEWS = ["articles", "kits"] as const;
export type StockView = (typeof STOCK_VIEWS)[number];

export const stockQuerySchema = listQuerySchema.extend({
  level: z.enum(STOCK_LEVELS).optional(),
  view: z.enum(STOCK_VIEWS).optional(),
  productType: z.string().max(120).optional(),
  supplierId: z.uuid().optional(),
});
export type StockQuery = z.infer<typeof stockQuerySchema>;

const quantity = z.coerce.number().int("errors.field.number").min(0, "errors.field.positive").max(1_000_000);
const isoDate = z.iso.date("errors.field.date");

/** Correction from a spot check: the user states what is on the shelf, the API books the difference. */
// ── Serial numbers ───────────────────────────────────────────────────────────

export const SERIAL_STATUS_VALUES = ["in_stock", "shipped", "quarantine", "scrapped", "exited"] as const;
export type SerialStatusValue = (typeof SERIAL_STATUS_VALUES)[number];
/** A serial number as typed or scanned: trimmed, upper-cased, 1 to 64 characters. */
export const serialSchema = z
  .string()
  .trim()
  .min(1, "errors.field.required")
  .max(64, "errors.field.tooLong")
  .transform((v) => v.toUpperCase());
/** The serial numbers of the units a movement concerns (optional unless the article is tracked). */
export const serialsSchema = z.array(serialSchema).max(10_000).optional();
export const serialsByVariantSchema = z.record(z.uuid(), z.array(serialSchema).max(10_000));
export const serialTrackingProductSchema = z.object({
  tracked: z.boolean(),
  /** Units already on the shelf, by variant, when tracking starts on a product with stock. */
  serialsByVariant: serialsByVariantSchema.optional(),
});
export type SerialTrackingProductInput = z.input<typeof serialTrackingProductSchema>;
export const serialTrackingSettingsSchema = z.object({ enabled: z.boolean() });
export const serialTrackingTypeSchema = z.object({ tracked: z.boolean(), serialsByVariant: serialsByVariantSchema.optional() });
export const serialLookupSchema = z.object({ q: z.string().trim().min(1).max(64) });
export const serialListQuerySchema = z.object({ status: z.enum(SERIAL_STATUS_VALUES).optional() });
export const shipmentSerialsSchema = z.object({
  lines: z
    .array(z.object({ lineId: z.uuid(), serials: z.array(serialSchema).max(10_000) }))
    .min(1)
    .refine(...uniqueBy("lineId")),
});
export type ShipmentSerialsInput = z.input<typeof shipmentSerialsSchema>;

export const stockAdjustmentSchema = z.object({
  variantId: z.uuid(),
  countedQuantity: quantity,
  /** Tracked article: the serials added (stock up) or missing (stock down). */
  serials: serialsSchema,
  note: z.string().trim().min(1, "errors.field.required").max(500, "errors.field.tooLong"),
  /** Day the shelf was counted; empty means now. Never in the future. */
  countedAt: z
    .union([z.literal(""), isoDate])
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .refine((v) => !v || v <= parisToday(), "errors.field.dateFuture"),
});
export type StockAdjustmentInput = z.input<typeof stockAdjustmentSchema>;

export const STOCK_EXIT_REASONS = ["test", "trade_show", "loan", "loss", "internal_use", "other"] as const;
export type StockExitReason = (typeof STOCK_EXIT_REASONS)[number];

/** Goods leaving without a parcel (tests, trade show, loan, breakage): no delivery note, just the ledger. */
export const stockExitSchema = z
  .object({
    variantId: z.uuid(),
    quantity: quantity.min(1, "errors.field.positive"),
    serials: serialsSchema,
    reason: z.enum(STOCK_EXIT_REASONS, "errors.field.required"),
    note: z.string().trim().max(500, "errors.field.tooLong").optional(),
    /** Confirmed by the user: units set aside for orders may be taken (they are released from them). */
    takeReserved: z.boolean().default(false),
  })
  .refine((v) => v.reason !== "other" || !!v.note, { message: "errors.field.required", path: ["note"] });
export type StockExitInput = z.input<typeof stockExitSchema>;
export type StockExitData = z.output<typeof stockExitSchema>;

export const stockCountCreateSchema = z.object({
  countedAt: isoDate,
  productType: z.string().max(120).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type StockCountCreateInput = z.input<typeof stockCountCreateSchema>;
export type StockCountCreateData = z.output<typeof stockCountCreateSchema>;

export const stockCountLinesSchema = z.object({
  lines: z
    .array(
      z.object({
        variantId: z.uuid(),
        countedQuantity: quantity.nullable(),
        serials: z.array(serialSchema).nullable().optional(),
      }),
    )
    .max(5000),
});
export type StockCountLinesInput = z.input<typeof stockCountLinesSchema>;
export type StockCountLinesData = z.output<typeof stockCountLinesSchema>;

export const valuationQuerySchema = z.object({ at: isoDate.optional() });
